// RevenueDashboard.tsx - Main Dashboard Container with Service Revenue Tab
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

import RevenueService, {
  RevenueKPIs,
  DailyAnalytics,
  ProviderPerformance,
  ServicePopularity,
  RevenueTransaction,
  DoctorEarnings,
  ReferrerEarnings,
  OwnerEarnings,
} from "../services/revenueService";

import {
  OverviewTab,
  TransactionsTab,
  DoctorsTab,
  ReferrersTab,
  OwnersTab,
  AnalyticsTab,
  ServiceRevenueTab,
} from "../components/revenue/tabs";
import { LoadingSpinner } from "../components";
import { DateRangePicker, TabButton } from "../components/revenue/shared";
import { ColorTheme } from "../components/revenue/types/revenue.types";

const RevenueDashboard: React.FC = () => {
  // State Management
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [loading, setLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Data State
  const [kpis, setKpis] = useState<RevenueKPIs | null>(null);
  const [dailyData, setDailyData] = useState<DailyAnalytics[]>([]);
  const [transactions, setTransactions] = useState<RevenueTransaction[]>([]);
  const [doctorEarnings, setDoctorEarnings] = useState<DoctorEarnings[]>([]);
  const [referrerEarnings, setReferrerEarnings] = useState<ReferrerEarnings[]>(
    [],
  );
  const [ownerEarnings, setOwnerEarnings] = useState<OwnerEarnings[]>([]);
  const [providerPerformance, setProviderPerformance] = useState<
    ProviderPerformance[]
  >([]);
  const [servicePopularity, setServicePopularity] = useState<
    ServicePopularity[]
  >([]);

  const { user } = useAuth();
  const userRoles = user?.roles || [];
  const userRole = userRoles.includes("admin") ? "admin" : "user";

  // Color Theme
  const colorTheme: ColorTheme = {
    primary: "#3b82f6",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    charity: "#ec4899",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray700: "#374151",
    gray900: "#111827",
    cardBg: "#ffffff",
  };

  // Initialize dates on mount
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    setEndDate(formatDate(today));
    setStartDate(formatDate(thirtyDaysAgo));
  }, []);

  // Load data when dates change
  useEffect(() => {
    if (startDate && endDate) {
      loadAllData();
    }
  }, [startDate, endDate]);

  const formatDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      // Calculate days for getDailyAnalytics
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      );

      const [
        kpisData,
        daily,
        transResponse,
        doctors,
        referrers,
        owners,
        providers,
        services,
      ] = await Promise.all([
        RevenueService.getRevenueKPIs(params),
        RevenueService.getDailyAnalytics(days),
        RevenueService.getTransactions(params),
        RevenueService.getDoctorEarnings(params),
        RevenueService.getReferrerEarnings(params),
        RevenueService.getOwnerEarnings(params),
        RevenueService.getProviderPerformance(params),
        RevenueService.getServicePopularity(params),
      ]);

      setKpis(kpisData);
      setDailyData(daily);
      setTransactions(transResponse.data);
      setDoctorEarnings(doctors);
      setReferrerEarnings(referrers);
      setOwnerEarnings(owners);
      setProviderPerformance(providers);
      setServicePopularity(services);
    } catch (error) {
      console.error("Error loading revenue data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Loading State
  if (loading) {
    return <LoadingSpinner color={colorTheme.primary} />;
  }

  return (
    <div
      style={{
        padding: "24px",
        background: colorTheme.gray50,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: colorTheme.gray900,
            margin: "0 0 8px 0",
          }}
        >
          💰 Revenue Dashboard
        </h1>
        <p style={{ color: colorTheme.gray600, margin: 0 }}>
          Track and analyze your clinic's revenue performance
        </p>
      </div>

      {/* Date Range Filter */}
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        colorTheme={colorTheme}
      />

      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "24px",
          borderBottom: `2px solid ${colorTheme.gray200}`,
          paddingBottom: "0",
          overflowX: "auto",
        }}
      >
        <TabButton
          active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
          icon="📊"
          label="Overview"
          colorTheme={colorTheme}
        />
        {userRole === "admin" && (
          <>
            <TabButton
              active={activeTab === "services"}
              onClick={() => setActiveTab("services")}
              icon="🔧"
              label="Service Revenue"
              colorTheme={colorTheme}
            />
            <TabButton
              active={activeTab === "transactions"}
              onClick={() => setActiveTab("transactions")}
              icon="💰"
              label="Transactions"
              colorTheme={colorTheme}
            />
            <TabButton
              active={activeTab === "doctors"}
              onClick={() => setActiveTab("doctors")}
              icon="👨‍⚕️"
              label="Workers"
              colorTheme={colorTheme}
            />
            <TabButton
              active={activeTab === "referrers"}
              onClick={() => setActiveTab("referrers")}
              icon="🤝"
              label="Referrers"
              colorTheme={colorTheme}
            />
            <TabButton
              active={activeTab === "owners"}
              onClick={() => setActiveTab("owners")}
              icon="👑"
              label="Sponsors"
              colorTheme={colorTheme}
            />
            <TabButton
              active={activeTab === "equipment"}
              onClick={() => setActiveTab("equipment")}
              icon="🏥"
              label="Equipment"
              colorTheme={colorTheme}
            />
            <TabButton
              active={activeTab === "analytics"}
              onClick={() => setActiveTab("analytics")}
              icon="📈"
              label="Analytics"
              colorTheme={colorTheme}
            />
          </>
        )}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "overview" && (
          <OverviewTab
            kpis={kpis}
            dailyData={dailyData}
            transactions={transactions}
            colorTheme={colorTheme}
            userRole={userRole}
          />
        )}

        {activeTab === "services" && userRole === "admin" && (
          <ServiceRevenueTab
            colorTheme={colorTheme}
            startDate={startDate}
            endDate={endDate}
          />
        )}

        {activeTab === "transactions" && userRole === "admin" && (
          <TransactionsTab
            transactions={transactions}
            colorTheme={colorTheme}
          />
        )}

        {activeTab === "doctors" && userRole === "admin" && (
          <DoctorsTab doctorEarnings={doctorEarnings} colorTheme={colorTheme} />
        )}

        {activeTab === "referrers" && userRole === "admin" && (
          <ReferrersTab
            referrerEarnings={referrerEarnings}
            colorTheme={colorTheme}
          />
        )}

        {activeTab === "owners" && userRole === "admin" && (
          <OwnersTab ownerEarnings={ownerEarnings} colorTheme={colorTheme} />
        )}

        {activeTab === "analytics" && userRole === "admin" && (
          <AnalyticsTab
            kpis={kpis}
            dailyData={dailyData}
            providerPerformance={providerPerformance}
            servicePopularity={servicePopularity}
            colorTheme={colorTheme}
          />
        )}
      </div>
    </div>
  );
};

export default RevenueDashboard;
